import React, { useState } from 'react';
import { Form, Input, InputNumber, Button, Space, Row, Col, Radio, Select, Switch } from 'antd';
import { PlusOutlined, CloseOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';

const { Option } = Select;

// Filters restrict/narrow the pool — they're only meaningful for RULE_BASED
// tabs (a MANUAL tab's membership is the explicit hand-picked list itself,
// nothing to filter). Ordering, below, is separate: it applies regardless of
// how content was added.
const FILTER_KINDS = [
  { key: 'typeFilter', label: 'Type' },
  { key: 'limit', label: 'Limit' },
  { key: 'maxPerCategory', label: 'Max per category', toolOnly: true },
];

function initialFilterRows(values) {
  if (!values) return [];
  const rows = [];
  if (values.typeFilter) rows.push('typeFilter');
  if (values.limit != null) rows.push('limit');
  if (values.maxPerCategory != null) rows.push('maxPerCategory');
  return rows;
}

// contentType/additionMethod combinations are all valid — see backend
// TabResolver.getTabContent for how each combination is interpreted at query
// time. Content itself (which categories/tools are in the tab) is managed
// separately, from the "+ Add Content" button below the tab once it exists.
//
// Ordering is independent of everything else: it says HOW the content is
// sorted (drag order, or by a date/usage-count field), regardless of whether
// that content was hand-picked (MANUAL) or filter-driven (RULE_BASED), and
// regardless of any Filter rules below. Filters only narrow WHICH content
// qualifies (RULE_BASED only) — they don't affect ordering semantics.
const TabForm = ({ form, initialValues, onFinish, submitLabel, loading, extraActions }) => {
  const [contentType, setContentType] = useState(initialValues?.contentType || 'CATEGORY');
  const [additionMethod, setAdditionMethod] = useState(initialValues?.additionMethod || 'MANUAL');
  // 'MANUAL' still exists on the backend as the invisible default sortMode
  // when nothing else was ever set, but it's not offered as a pickable
  // option — Custom does the same thing (drag order) and is the real default.
  const [sortMode, setSortMode] = useState(
    initialValues?.sortMode && initialValues.sortMode !== 'MANUAL' ? initialValues.sortMode : 'CUSTOM'
  );
  const [filterRows, setFilterRows] = useState(() => initialFilterRows(initialValues));
  const sortField = Form.useWatch('sortField', form);

  const availableFilterKinds = FILTER_KINDS.filter((k) => !k.toolOnly || contentType === 'TOOL');

  const resetFilterValue = (kind) => {
    switch (kind) {
      case 'typeFilter':
        form.setFieldValue('typeFilter', undefined);
        break;
      case 'limit':
        form.setFieldValue('limit', undefined);
        break;
      case 'maxPerCategory':
        form.setFieldValue('maxPerCategory', undefined);
        break;
      default:
        break;
    }
  };

  const handleAddFilter = () => {
    const next = availableFilterKinds.find((k) => !filterRows.includes(k.key));
    if (next) setFilterRows((prev) => [...prev, next.key]);
  };

  const handleChangeFilterKind = (index, newKind) => {
    resetFilterValue(filterRows[index]);
    setFilterRows((prev) => prev.map((k, i) => (i === index ? newKind : k)));
  };

  const handleRemoveFilter = (index) => {
    resetFilterValue(filterRows[index]);
    setFilterRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearFilters = () => {
    filterRows.forEach(resetFilterValue);
    setFilterRows([]);
  };

  const renderFilterValue = (kind) => {
    switch (kind) {
      case 'typeFilter':
        return (
          <Form.Item name="typeFilter" noStyle>
            <Select placeholder="Select type..." allowClear style={{ width: '100%' }}>
              <Option value="VIDEO">Video</Option>
              <Option value="IMAGE">Image</Option>
              {contentType === 'CATEGORY' && <Option value="MIX">Mix (video + image)</Option>}
            </Select>
          </Form.Item>
        );
      case 'limit':
        return (
          <Form.Item name="limit" noStyle>
            <InputNumber min={1} placeholder="e.g. 20" style={{ width: '100%' }} />
          </Form.Item>
        );
      case 'maxPerCategory':
        return (
          <Form.Item name="maxPerCategory" noStyle>
            <InputNumber min={1} placeholder="e.g. 3" style={{ width: '100%' }} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={
        initialValues || {
          isActive: true,
          contentType: 'CATEGORY',
          additionMethod: 'MANUAL',
          sortMode: 'CUSTOM',
          sortDirection: 'ASC',
        }
      }
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item name="name" label="Tab Name" rules={[{ required: true, message: 'Please enter a tab name' }]}>
            <Input placeholder="e.g. New, Trending, Videos" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="contentType" label="What does this tab show?" rules={[{ required: true }]}>
        <Radio.Group
          onChange={(e) => {
            setContentType(e.target.value);
            // MIX only makes sense for categories — clear it if switching to Tools
            if (e.target.value === 'TOOL' && form.getFieldValue('typeFilter') === 'MIX') {
              form.setFieldValue('typeFilter', undefined);
            }
            // maxPerCategory only makes sense for Tools — clear it (and its filter row) if switching to Categories
            if (e.target.value === 'CATEGORY') {
              form.setFieldValue('maxPerCategory', undefined);
              setFilterRows((prev) => prev.filter((k) => k !== 'maxPerCategory'));
            }
            // Usage Count sorting is Tool-only — fall back to Created At if switching to Categories
            if (e.target.value === 'CATEGORY' && form.getFieldValue('sortField') === 'USAGE_COUNT') {
              form.setFieldValue('sortField', 'CREATED_AT');
              form.setFieldValue('usageWindowDays', undefined);
            }
            // Stale 'MANUAL' sortMode (from before Manual was removed as an
            // Ordering option) isn't pickable anymore — normalize to Custom
            if (form.getFieldValue('sortMode') === 'MANUAL') {
              form.setFieldValue('sortMode', 'CUSTOM');
              setSortMode('CUSTOM');
            }
          }}
        >
          <Radio value="CATEGORY">Categories</Radio>
          <Radio value="TOOL">Tools</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item name="additionMethod" label="How is content added?" rules={[{ required: true }]}>
        <Radio.Group onChange={(e) => setAdditionMethod(e.target.value)}>
          <Radio value="MANUAL">Manual (hand-picked)</Radio>
          <Radio value="RULE_BASED">Rule-based</Radio>
        </Radio.Group>
      </Form.Item>

      {/* Ordering — always applies, regardless of Manual vs Rule-based */}
      <div className="rule-filter-panel">
        <div className="rule-filter-header">
          <span className="rule-filter-title">Order</span>
        </div>
        <div style={{ display: 'flex', gap: 10, width: sortMode === 'DYNAMIC' ? '100%' : 'auto' }}>
          <div style={{ width: sortMode === 'DYNAMIC' ? (sortField === 'USAGE_COUNT' ? '26%' : '34%') : 220 }}>
            <div className="rule-row-hint" style={{ marginBottom: 4 }}>Order by</div>
            <Form.Item name="sortMode" noStyle rules={[{ required: true }]}>
              <Select style={{ width: '100%' }} onChange={setSortMode}>
                <Option value="CUSTOM">Drag and drop</Option>
                <Option value="DYNAMIC">Specify</Option>
              </Select>
            </Form.Item>
          </div>
          {sortMode === 'DYNAMIC' && (
            <>
              <div style={{ width: sortField === 'USAGE_COUNT' ? '25%' : '33%' }}>
                <div className="rule-row-hint" style={{ marginBottom: 4 }}>Field</div>
                <Form.Item name="sortField" noStyle rules={[{ required: true }]}>
                  <Select
                    style={{ width: '100%' }}
                    onChange={(val) => {
                      if (val !== 'USAGE_COUNT') form.setFieldValue('usageWindowDays', undefined);
                    }}
                  >
                    <Option value="CREATED_AT">Created At</Option>
                    <Option value="UPDATED_AT">Updated At</Option>
                    {contentType === 'TOOL' && <Option value="USAGE_COUNT">Usage Count</Option>}
                  </Select>
                </Form.Item>
              </div>
              <div style={{ width: sortField === 'USAGE_COUNT' ? '25%' : '33%' }}>
                <div className="rule-row-hint" style={{ marginBottom: 4 }}>Direction</div>
                <Form.Item name="sortDirection" noStyle rules={[{ required: true }]}>
                  <Select style={{ width: '100%' }}>
                    <Option value="ASC"><SortAscendingOutlined /> Ascending</Option>
                    <Option value="DESC"><SortDescendingOutlined /> Descending</Option>
                  </Select>
                </Form.Item>
              </div>
              {sortField === 'USAGE_COUNT' && (
                <div style={{ width: '24%' }}>
                  <div className="rule-row-hint" style={{ marginBottom: 4 }}>Usage window (days)</div>
                  <Form.Item name="usageWindowDays" noStyle>
                    <InputNumber
                      min={1}
                      style={{ width: '100%' }}
                      placeholder="All time"
                      autoComplete="off"
                      data-1p-ignore="true"
                      data-lpignore="true"
                    />
                  </Form.Item>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {additionMethod === 'RULE_BASED' && (
        <div className="rule-filter-panel">
          <div className="rule-filter-header">
            <span className="rule-filter-title">Filter</span>
            {filterRows.length > 0 && (
              <span className="rule-filter-clear" onClick={handleClearFilters}>Clear filters</span>
            )}
          </div>

          {filterRows.length === 0 && (
            <div className="rule-filter-empty">No rules yet</div>
          )}

          {filterRows.map((kind, index) => (
            <div className="rule-row" key={`${kind}-${index}`}>
              <Select
                value={kind}
                onChange={(newKind) => handleChangeFilterKind(index, newKind)}
                className="rule-row-kind"
              >
                {availableFilterKinds
                  .filter((k) => k.key === kind || !filterRows.includes(k.key))
                  .map((k) => (
                    <Option key={k.key} value={k.key}>{k.label}</Option>
                  ))}
              </Select>
              <div className="rule-row-value">{renderFilterValue(kind)}</div>
              <CloseOutlined className="rule-row-remove" onClick={() => handleRemoveFilter(index)} />
            </div>
          ))}

          {filterRows.length < availableFilterKinds.length && (
            <Button type="text" icon={<PlusOutlined />} onClick={handleAddFilter} className="rule-add-button">
              Add rule
            </Button>
          )}
        </div>
      )}
      <Form.Item style={{ marginTop: 24 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {submitLabel}
          </Button>
          {extraActions}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default TabForm;
